import React, { useEffect, useState } from 'react';
import {
  CtaButton,
  ModalContainer,
  SelectInput,
  TextInput,
  TogglerButtonInput,
} from '@tassomutuo-backoffice/ui';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import {
  DEAL_STAGE_LABEL_VALUE_OBJ,
  DealStage,
  Lead,
} from '@tassomutuo-backoffice/types';
import formatMoney, {
  formatNumberWithThousandsSeparator,
  removeThousandSeparator,
} from '../../../utils/formatMoney';
import { getMotivations } from '../../../utils/dropMotivation';

interface IFormInputs {
  dealStage: DealStage;
  loanAmount: string;
  propertyValue: string;
  note: string;
  droppedMotivation: string;
  attoDate: Date | null;
  commission: string;
  appointmentDate: Date | null;
  newAppointmentDate: Date | null;
  appointmentTime: string | null;
  research: string;
  appointmentLocation: string;
  meeting_done: boolean;
}


interface IProps {
  open: boolean;
  handleClose: () => void;
  lead?: Lead;
  updateLeadStatus: (data: IFormInputs) => Promise<void>;
}

const ModalLeadDropForm = ({
  open,
  handleClose,
  lead,
  updateLeadStatus,
}: IProps) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<IFormInputs>({
    defaultValues: {
      dealStage: lead?.properties.dealstage,
      loanAmount: lead?.properties.loan_amount
        ? formatNumberWithThousandsSeparator(
            formatMoney(+lead?.properties.loan_amount)
          )
        : '',
      propertyValue: lead?.properties.property_value
        ? formatNumberWithThousandsSeparator(
            formatMoney(+lead?.properties.property_value)
          )
        : '',
      note: '',
      droppedMotivation: '',
      attoDate: lead?.properties.data_atto_concluso
        ? new Date(lead?.properties.data_atto_concluso)
        : null,
      commission: lead?.properties.amount
        ? formatNumberWithThousandsSeparator(
            formatMoney(+lead?.properties.amount)
          )
        : '',
      appointmentDate: lead?.properties.appointment_date
        ? new Date(lead?.properties.appointment_date)
        : null,
      appointmentTime: lead?.properties.appointment_hour || '',
      appointmentLocation: lead?.properties.appointment_location || '',
    },
  });

  useEffect(() => {
    if (lead) {
      reset({
        dealStage: lead?.properties.dealstage,
        loanAmount: lead?.properties.loan_amount
          ? formatNumberWithThousandsSeparator(
              formatMoney(+lead?.properties.loan_amount)
            )
          : '',
        propertyValue: lead?.properties.property_value
          ? formatNumberWithThousandsSeparator(
              formatMoney(+lead?.properties.property_value)
            )
          : '',
        note: '',
        droppedMotivation: '',
        attoDate: lead?.properties.data_atto_concluso
          ? new Date(lead?.properties.data_atto_concluso)
          : null,
        commission: lead?.properties.amount
          ? formatNumberWithThousandsSeparator(
              formatMoney(+lead?.properties.amount)
            )
          : '',
        appointmentDate: lead?.properties.appointment_date
          ? new Date(lead?.properties.appointment_date)
          : null,
        appointmentTime: lead?.properties.appointment_hour || '',
        appointmentLocation: lead?.properties.appointment_location || '',
      });
    }
  }, [lead]);

  const [isLoading, setIsLoading] = useState(false);

  const onCloseHandler = () => {
    handleClose();
    reset({});
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    let dataToSend = { ...data };
    dataToSend.dealStage = DEAL_STAGE_LABEL_VALUE_OBJ.DROPPED.value;
    if (data.loanAmount) {
      dataToSend = {
        ...dataToSend,
        loanAmount: removeThousandSeparator(data.loanAmount),
      };
    }
    if (data.propertyValue) {
      dataToSend = {
        ...dataToSend,
        propertyValue: removeThousandSeparator(data.propertyValue),
      };
    }
    if (data.commission) {
      dataToSend = {
        ...dataToSend,
        commission: removeThousandSeparator(data.commission),
      };
    }
    try {
      setIsLoading(true);
      await updateLeadStatus(dataToSend);
      onCloseHandler();
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <ModalContainer
      open={open}
      handleClose={onCloseHandler}
      title="Fornisci aggiornamento"
      containerSx={{ maxWidth: '500px' }}
      mtHeader={0}
    >
      <Box
        width="100%"
        maxWidth="450px"
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="droppedMotivation"
          rules={{
            required: 'Seleziona una motivazione',
          }}
          shouldUnregister
          render={({ field }) => (
            <SelectInput
              label="Motivazione"
              helperText={errors?.droppedMotivation?.message}
              error={!!errors?.droppedMotivation}
              selectOptions={[
                { label: 'Seleziona una motivazione', value: '' },
                ...getMotivations(lead?.properties.dealstage),
              ]}
              {...field}
            />
          )}
        />

        {(lead?.properties.dealstage ===
          DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO.value ||
          lead?.properties.dealstage ===
            DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_CONFERMARE.value ||
          lead?.properties.dealstage ===
            DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_RIFISSARE.value ||
          lead?.properties.dealstage ===
            DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_ANNULLATO_STB_15GG
              .value) && (
          <Controller
            control={control}
            name="meeting_done"
            rules={{
              validate: (value) =>
                value === undefined || value === null
                  ? "Per favore seleziona un'opzione"
                  : true,
            }}
            shouldUnregister
            render={({ field }) => (
              <TogglerButtonInput
                label="Appuntamento"
                helperText={errors?.meeting_done?.message}
                error={!!errors?.meeting_done}
                selectOptions={[
                  { label: 'Svolto', value: 'true' },
                  { label: 'Non Svolto', value: 'false' },
                ]}
                {...field}
              />
            )}
          />
        )}

        {(lead?.properties.dealstage !==
          DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_CONFERMATO.value &&
          lead?.properties.dealstage !==
            DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_CONFERMARE.value &&
          lead?.properties.dealstage !==
            DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_DA_RIFISSARE.value &&
          lead?.properties.dealstage !==
            DEAL_STAGE_LABEL_VALUE_OBJ.APPUNTAMENTO_ANNULLATO_STB_15GG
              .value) && (                
                <Controller
                control={control}
                  name="meeting_done"
                  defaultValue={true}
                  render={({ field }) => (
                  <input type="hidden" {...field} value={field.value ? 'true' : 'false'} />
                )}
                />                
        )}

        <Controller
          control={control}
          name="note"
          rules={{
            required:
              'Per favore inserisci le note. Per noi sono molto importanti per capire lo stato di avanzamento',
          }}
          shouldUnregister
          render={({ field }) => (
            <TextInput
              type="text"
              label="Note"
              helperText={errors?.note?.message}
              error={!!errors?.note}
              multiline
              minRows={3}
              {...field}
            />
          )}
        />
        <Box display="flex" justifyContent="center" mt={4}>
          <CtaButton variant="contained" type="submit" loading={isLoading}>
            SALVA
          </CtaButton>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export default ModalLeadDropForm;
